import { ReactNode, useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { getCurrentTimeET, getTimezoneAbbreviation, DISPLAY_TIMEZONE } from "../utils/timezone";

interface LayoutProps {
  children: ReactNode;
}

function Layout({ children }: LayoutProps) {
  const [currentTime, setCurrentTime] = useState(getCurrentTimeET());
  const [tzAbbrev, setTzAbbrev] = useState(getTimezoneAbbreviation());

  // Update time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(getCurrentTimeET());
      setTzAbbrev(getTimezoneAbbreviation());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="app-layout">
      <aside className="sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            📊 <span>Still Here Admin</span>
          </div>
          <div
            className="timezone-indicator"
            title={`All times shown in ${DISPLAY_TIMEZONE}`}
            style={{
              fontSize: "0.75rem",
              color: "#64748b",
              marginTop: "0.25rem",
              display: "flex",
              alignItems: "center",
              gap: "0.25rem",
            }}
          >
            🕐 {currentTime} {tzAbbrev}
          </div>
        </div>

        <nav className="sidebar-nav">
          <NavLink to="/" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`} end>
            <HomeIcon />
            <span>Dashboard</span>
          </NavLink>

          <NavLink
            to="/users"
            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
          >
            <UsersIcon />
            <span>User Growth</span>
          </NavLink>

          <NavLink
            to="/viral"
            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
          >
            <ViralIcon />
            <span>Viral Metrics</span>
          </NavLink>

          <NavLink
            to="/revenue"
            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
          >
            <RevenueIcon />
            <span>Revenue</span>
          </NavLink>

          <NavLink to="/sms" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
            <SmsIcon />
            <span>SMS Monitor</span>
          </NavLink>

          <NavLink
            to="/health"
            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
          >
            <HealthIcon />
            <span>System Health</span>
          </NavLink>
        </nav>
      </aside>

      <main className="main-content">{children}</main>
    </div>
  );
}

// Simple SVG Icons
function HomeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function ViralIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </svg>
  );
}

function RevenueIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  );
}

function SmsIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function HealthIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </svg>
  );
}

export default Layout;
